/**
 * copilotDock — pure state reducer for the persistent Argo Copilot dock.
 *
 * The dock is the floating "Ask Argo" launcher (bottom-right) plus the
 * expandable chat drawer mounted at the app-shell level in App.tsx. Its
 * open/close/pulse behavior is a small state machine, extracted here as a
 * pure reducer so it can be unit-tested without React and so the contract
 * is explicit:
 *
 * - Collapsed by default, with a subtle attention pulse on the launcher.
 * - The pulse clears PERMANENTLY the first time the dock is opened (the
 *   affordance has done its job — a forever-pulsing button is noise).
 * - Route changes never reset the dock: it persists across every view
 *   (`route-change` is an identity transition that returns the SAME state
 *   reference, so React can skip re-rendering on navigation).
 * - A handed-in query (Dashboard "Ask Argo" buttons, ⌘K free text) opens
 *   the dock exactly like a manual open.
 *
 * All transitions are idempotent: dispatching `open` while open (or `close`
 * while closed) returns the same reference, never a new object.
 */

export interface DockState {
  /** True while the chat drawer is expanded. */
  open: boolean;
  /** Subtle pulse on the collapsed launcher — true until the first open. */
  pulse: boolean;
  /** Whether the dock has ever been opened this session. */
  openedOnce: boolean;
  /**
   * True while the drawer was opened BY the morning standup ('auto-standup')
   * — the one sanctioned unprompted open. Cleared on close; absent on every
   * manual open, so surfaces can tell "the system spoke first" apart from
   * "the operator asked".
   */
  standup?: boolean;
}

export type DockAction =
  | { type: 'open' }
  | { type: 'close' }
  | { type: 'toggle' }
  /** A query handed to the dock from elsewhere in the app — always opens. */
  | { type: 'query' }
  /**
   * The morning standup opens the dock UNPROMPTED — but only if the operator
   * hasn't already opened (or been shown) the dock this session. Never
   * hijacks an open drawer; at most once per session by construction.
   */
  | { type: 'auto-standup' }
  /** The active view changed — the dock persists untouched. */
  | { type: 'route-change' };

/** Collapsed by default, pulsing until the operator opens it once. */
export const initialDockState: DockState = {
  open: false,
  pulse: true,
  openedOnce: false,
};

export function dockReducer(state: DockState, action: DockAction): DockState {
  switch (action.type) {
    case 'open':
    case 'query':
      if (state.open) return state;
      return { open: true, pulse: false, openedOnce: true };
    case 'auto-standup':
      // Unprompted open, but never a trap: no-op while already open AND
      // after any first open this session — it can fire at most once.
      if (state.open || state.openedOnce) return state;
      return { open: true, pulse: false, openedOnce: true, standup: true };
    case 'close':
      if (!state.open) return state;
      // Closing always drops the standup flag — the unprompted moment is
      // over; every subsequent open is operator-initiated.
      return { open: false, pulse: state.pulse, openedOnce: state.openedOnce };
    case 'toggle':
      return dockReducer(state, { type: state.open ? 'close' : 'open' });
    case 'route-change':
      return state;
    default:
      return state;
  }
}
